"use client"

import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import Sidebar from "../../components/Sidebar"
import Topbar from "../../components/Topbar"
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi"
import toast, { Toaster } from "react-hot-toast"
import { Link } from "react-router-dom"
import CreateAdminModal from "../../components/models/CreateAdminModal"
import EditAdminModal from "../../components/models/EditAdminModal"
import DeleteAdminModal from "../../components/models/DeleteAdminModal"

const Admins = () => {
  const baseUrl = process.env.REACT_APP_APIURL
  const [token] = React.useState(sessionStorage.getItem("token"))
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = React.useState("")
  const [filterRole, setFilterRole] = React.useState("All")
  const [itemsPerPage, setItemsPerPage] = React.useState(10)
  const [currentPage, setCurrentPage] = React.useState(1)

  // Modal states
  const [showCreateModal, setShowCreateModal] = React.useState(false)
  const [showEditModal, setShowEditModal] = React.useState(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [selectedAdmin, setSelectedAdmin] = React.useState(null)

  // Fetch all admins
  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-list"],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}api/admin/all-admins`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return res.data
    },
    enabled: !!token,
  })

  // Delete admin mutation
  const deleteAdminMutation = useMutation({
    mutationFn: async (adminId) => {
      return axios.delete(`${baseUrl}api/admin/delete-admin/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    },
    onSuccess: () => {
      toast.success("Admin deleted successfully!")
      queryClient.invalidateQueries({ queryKey: ["admin-list"] })
      setShowDeleteModal(false)
      setSelectedAdmin(null)
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to delete admin.")
    },
  })

  const allAdmins = apiResponse?.data || []

  // Filter admins
  const filteredAdmins = React.useMemo(() => {
    let filtered = allAdmins

    // Role filter
    if (filterRole !== "All") {
      filtered = filtered.filter((admin) => admin.role && admin.role.toLowerCase() === filterRole.toLowerCase())
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((admin) => {
        return (
          admin.fullname?.toLowerCase().includes(query) ||
          admin.email?.toLowerCase().includes(query) ||
          admin.role?.toLowerCase().includes(query)
        )
      })
    }

    return filtered
  }, [allAdmins, filterRole, searchQuery])

  // Paginate admins
  const paginatedAdmins = React.useMemo(() => {
    if (itemsPerPage === "all") {
      return filteredAdmins
    }
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredAdmins.slice(startIndex, endIndex)
  }, [filteredAdmins, itemsPerPage, currentPage])

  const totalPages = React.useMemo(() => {
    if (itemsPerPage === "all") return 1
    return Math.ceil(filteredAdmins.length / itemsPerPage)
  }, [filteredAdmins.length, itemsPerPage])

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value === "all" ? "all" : Number(e.target.value))
    setCurrentPage(1)
  }

  const handleEditClick = (admin) => {
    setSelectedAdmin(admin)
    setShowEditModal(true)
  }

  const handleDeleteClick = (admin) => {
    setSelectedAdmin(admin)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = () => {
    if (selectedAdmin) {
      deleteAdminMutation.mutate(selectedAdmin._id)
    }
  }

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Admin Management</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Admins</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center pt-3">
                    <h5 className="card-title p-0 m-0">
                      Admins List <span>| {isLoading ? "Loading..." : `${filteredAdmins.length} Admins Found`}</span>
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-primary ms-2 d-inline-flex align-items-center gap-2"
                        onClick={() => setShowCreateModal(true)}
                        disabled={isLoading}
                      >
                        <FiPlus size={16} /> Create Admin
                      </button>
                    </div>
                  </div>
                  <hr />

                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="searchQuery" className="form-label small fw-bold">
                        Search Admins
                      </label>
                      <input
                        type="text"
                        id="searchQuery"
                        className="form-control form-control-sm"
                        placeholder="Search by Name, Email, or Role..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center p-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading admins...</span>
                      </div>
                    </div>
                  ) : isError ? (
                    <div className="alert alert-danger text-center" role="alert">
                      <h4 className="alert-heading">Error Loading Admins!</h4>
                      <p>{error?.response?.data?.message || error?.message || "An unexpected error occurred."}</p>
                    </div>
                  ) : allAdmins.length === 0 ? (
                    <div className="alert alert-info text-center" role="alert">
                      No admins found. Create one to get started.
                    </div>
                  ) : filteredAdmins.length === 0 ? (
                    <div className="alert alert-warning text-center" role="alert">
                      No admins match the applied filters.
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <label htmlFor="itemsPerPage" className="form-label mb-0 fw-bold small">
                            Items per page:
                          </label>
                          <select
                            id="itemsPerPage"
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isLoading}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value="all">All</option>
                          </select>
                        </div>
                        <div className="text-muted small">
                          Showing{" "}
                          {paginatedAdmins.length > 0
                            ? (currentPage - 1) * (itemsPerPage === "all" ? filteredAdmins.length : itemsPerPage) + 1
                            : 0}{" "}
                          to{" "}
                          {Math.min(
                            currentPage * (itemsPerPage === "all" ? filteredAdmins.length : itemsPerPage),
                            filteredAdmins.length,
                          )}{" "}
                          of {filteredAdmins.length}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-striped table-hover table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th scope="col" className="text-center">
                                #
                              </th>
                              <th scope="col">Full Name</th>
                              <th scope="col">Email</th>
                              <th scope="col" className="text-center">
                                Role
                              </th>
                              <th scope="col" className="text-center">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedAdmins.map((admin, index) => (
                              <tr key={admin._id}>
                                <td className="text-center">
                                  {(currentPage - 1) * (itemsPerPage === "all" ? 0 : itemsPerPage) + index + 1}
                                </td>
                                <td className="fw-semibold">{admin.fullname}</td>
                                <td>{admin.email}</td>
                                <td className="text-center">
                                  <span
                                    className={`badge rounded-pill px-2 py-1 ${
                                      admin.role === "super-admin"
                                        ? "bg-danger"
                                        : admin.role === "admin"
                                          ? "bg-primary"
                                          : "bg-secondary"
                                    }`}
                                  >
                                    {admin.role}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => handleEditClick(admin)}
                                    title="Edit Admin"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteClick(admin)}
                                    title="Delete Admin"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1 || itemsPerPage === "all"}
                          >
                            Previous
                          </button>
                          <div className="d-flex align-items-center px-2">
                            <span className="small text-muted">
                              Page {itemsPerPage === "all" ? 1 : currentPage} of {totalPages}
                            </span>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || itemsPerPage === "all"}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Modals */}
      <CreateAdminModal show={showCreateModal} onClose={() => setShowCreateModal(false)} />
      <EditAdminModal
        show={showEditModal}
        admin={selectedAdmin}
        onClose={() => {
          setShowEditModal(false)
          setSelectedAdmin(null)
        }}
      />
      <DeleteAdminModal
        show={showDeleteModal}
        admin={selectedAdmin}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedAdmin(null)
        }}
        isLoading={deleteAdminMutation.isLoading}
      />
    </>
  )
}

export default Admins
